import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useReactFlow } from "reactflow";
import {
  useGetAllFacilitiesForSearch,
  useGetFacilityWholeList
} from "../../../../../../api/cultivations";
import { useGetFacilityWholeList as useGetSupplierFacilityWholeList } from "../../../../../../api/supplierFarmGate";
import { DrawingContext } from "../../../../../../components/drawing-tool/DrawingTool";
import { isNodeLabelExists } from "../../../../../../components/drawing-tool/utils";
import { NEW_FACILITY_KEY } from "../../../../../../config/constants";
import { FacilityType } from "../../../../../../config/enums/FacilityType.enum";
import { NodeLabelExistError } from "../../../../../../config/enums/NodeLabelError.enum";
import { errorView, successMessage } from "../../../../../../helpers/ErrorHandler";
import { hasValidationErrors } from "../../../../../../utils/utils";
import { DrawingForIngredientContext } from "../../../../../supplier_drawing_tool/DrawingToolForIngredient";
import { getInternalStorageApiHooks } from "../internalStorageApi.config";
import { getInternalStorageGateConfig } from "../internalStorageGate.config";
import {
  InternalStorageFormValues,
  InternalStorageGate,
  InternalStorageModalData,
  InternalStorageToolVariant,
  SaveDocumentDataHandle
} from "../internalStorage.types";
import {
  buildDefaultFormValues,
  buildInternalStorageNodeLabel,
  buildInternalStoragePayload,
  isNewFacilitySelection,
  isSpecificDataType,
  mapDropdownFacilities,
  mapFormValuesToLoadedData,
  mapOriginalStorageData,
  mapSearchFacilities,
  normalizeInternalStoragePayload
} from "../internalStorage.utils";
import { getInternalStorageVariantConfig } from "../internalStorageVariant.config";

interface UseInternalStorageBaseScreenOptions {
  modalData: InternalStorageModalData;
  gate: InternalStorageGate;
  variant?: InternalStorageToolVariant;
}

export const useInternalStorageBaseScreen = ({
  modalData,
  gate,
  variant = "mapping"
}: UseInternalStorageBaseScreenOptions) => {
  const mappingContext = useContext(DrawingContext);
  const supplierContext = useContext(DrawingForIngredientContext);
  const drawingContext = variant === "supplier" ? supplierContext : mappingContext;
  const variantConfig = getInternalStorageVariantConfig(variant);
  const gateConfig = getInternalStorageGateConfig(gate);
  const apiHooks = getInternalStorageApiHooks(gate, variant);

  const { nodeItem } = modalData;
  const gateId = (modalData[gateConfig.gateIdKey] as string) || "";

  const {
    nodeItemId,
    lockStatus = false,
    deleteLockStatus = lockStatus,
    setNodeItem,
    setChartNodes,
    saveDrawing,
    chartEdges,
    chartNodes
  } = drawingContext as typeof mappingContext;

  const { deleteElements, getEdges } = useReactFlow();

  const notesRef = useRef<SaveDocumentDataHandle | null>(null);
  const newlyCreatedFacilityRef = useRef<string | null>(null);
  const loadedStorageIdRef = useRef<string | null>(null);
  const loadedOriginalIdRef = useRef<string | null>(null);

  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [metricsExpanded, setMetricsExpanded] = useState(true);
  const [notesExpanded, setNotesExpanded] = useState(true);
  const [showFacilityRegister, setShowFacilityRegister] = useState(false);
  const [visibleFacilityModal, setVisibleFacilityModal] = useState(false);
  const [savedInternalStorageId, setSavedInternalStorageId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [loadedData, setLoadedData] = useState<any>(null);
  const [originalData, setOriginalData] = useState<any>(null);

  const internalStorageId =
    nodeItem?.data?.reference?.internalStorageId || savedInternalStorageId || null;

  const simulateOriginalId =
    variant === "simulate" ? loadedData?.originalId ?? null : null;

  useEffect(() => {
    if (nodeItem?.data?.reference?.internalStorageId) {
      setSavedInternalStorageId(null);
    }
  }, [nodeItem?.data?.reference?.internalStorageId]);

  const connectedEdges = getEdges().filter(
    edge => edge.source === nodeItemId || edge.target === nodeItemId
  );

  const mappingFacilitiesQuery = useGetFacilityWholeList(FacilityType.STORAGE);
  const supplierFacilitiesQuery = useGetSupplierFacilityWholeList(FacilityType.STORAGE);
  const searchFacilitiesQuery = useGetAllFacilitiesForSearch(FacilityType.STORAGE);

  const dropdownFacilitiesQuery =
    variant === "supplier" ? supplierFacilitiesQuery : mappingFacilitiesQuery;

  const facilities = useMemo(
    () =>
      mapDropdownFacilities(
        dropdownFacilitiesQuery.data || [],
        variantConfig.supportsNewFacility
      ),
    [dropdownFacilitiesQuery.data, variantConfig.supportsNewFacility]
  );

  const searchFacilities = useMemo(
    () => mapSearchFacilities(searchFacilitiesQuery.availableFacilities || []),
    [searchFacilitiesQuery.availableFacilities]
  );

  const defaultFacilityId = useMemo(() => {
    const list = dropdownFacilitiesQuery.data || [];
    return list.find((item: { value?: string }) => item.value !== NEW_FACILITY_KEY)?.value;
  }, [dropdownFacilitiesQuery.data]);

  const { refetch: refetchStorage } = apiHooks.useGetById(internalStorageId);
  const { refetch: refetchOriginal } = apiHooks.useGetOriginalById(simulateOriginalId);

  const { mutate: createInternalStorage, isLoading: createLoading } = apiHooks.useCreate();
  const { mutate: updateInternalStorage, isLoading: updateLoading } = apiHooks.useUpdate();
  const { mutate: deleteInternalStorage, isLoading: deleteLoading } = apiHooks.useDelete();

  const existingStorageName = loadedData?.internalStorageName ?? "";

  const formValues = useMemo(
    () => buildDefaultFormValues(loadedData, defaultFacilityId, variant),
    [loadedData, defaultFacilityId, variant]
  );

  const form = useForm<InternalStorageFormValues>({
    defaultValues: formValues,
    values: formValues
  });

  const {
    register,
    control,
    handleSubmit,
    getValues,
    setFocus,
    setError,
    clearErrors,
    watch,
    setValue,
    formState: { errors }
  } = form;

  const watchedDataType = watch("dataType");
  const watchedWarehouseName = watch("warehouseName");
  const isDefaultDataType = variantConfig.supportsDataType
    ? isSpecificDataType(watchedDataType)
    : true;

  const isFormDisabled = !!lockStatus;
  const saveDisabled =
    hasValidationErrors(errors) || lockStatus || dropdownFacilitiesQuery.isLoading;

  const loadStorageData = useCallback(async () => {
    if (!internalStorageId) {
      loadedStorageIdRef.current = null;
      setLoadedData(null);
      setOriginalData(null);
      loadedOriginalIdRef.current = null;
      return;
    }

    if (loadedStorageIdRef.current === internalStorageId) {
      return;
    }

    setIsLoadingData(true);
    try {
      const result = await refetchStorage?.();
      const storageData = result?.data ?? null;
      setLoadedData(storageData);
      loadedStorageIdRef.current = internalStorageId;
      loadedOriginalIdRef.current = null;
      setOriginalData(null);
    } finally {
      setIsLoadingData(false);
    }
  }, [internalStorageId, refetchStorage]);

  useEffect(() => {
    if (variant !== "simulate" || !simulateOriginalId) {
      return;
    }

    if (loadedOriginalIdRef.current === simulateOriginalId) {
      return;
    }

    loadedOriginalIdRef.current = simulateOriginalId;
    void refetchOriginal?.().then(result => {
      setOriginalData(mapOriginalStorageData(result?.data));
    });
  }, [refetchOriginal, simulateOriginalId, variant]);

  useEffect(() => {
    if (newlyCreatedFacilityRef.current) {
      newlyCreatedFacilityRef.current = null;
      return;
    }

    if (internalStorageId) {
      void loadStorageData();
      return;
    }

    loadedStorageIdRef.current = null;
    loadedOriginalIdRef.current = null;
    setLoadedData(null);
    setOriginalData(null);
    if (defaultFacilityId) {
      setValue("warehouseName", defaultFacilityId);
    }
    setValue("areaUtilization", 100);
  }, [defaultFacilityId, internalStorageId, loadStorageData, setValue]);

  const updateDrawingNode = useCallback(
    async (updatedNode: any, successMessageText: string) => {
      try {
        if (variantConfig.supportsNotes && updatedNode?.data?.reference?.internalStorageId) {
          await notesRef.current?.saveDocumentAndNote(
            updatedNode.data.reference.internalStorageId
          );
        }
        const updatedChartNodes = chartNodes.map((node: any) =>
          node.id === nodeItem?.id ? updatedNode : node
        );
        setChartNodes(updatedChartNodes);
        setNodeItem(updatedNode);
        saveDrawing(chartEdges, updatedChartNodes);
        successMessage(successMessageText);
      } catch (error) {
        errorView(error);
      }
    },
    [
      chartEdges,
      chartNodes,
      nodeItem?.id,
      saveDrawing,
      setChartNodes,
      setNodeItem,
      variantConfig.supportsNotes
    ]
  );

  const onNodesDelete = useCallback(() => {
    if (variantConfig.supportsNotes) {
      notesRef.current?.deleteComment();
    }

    if (variant === "mapping") {
      deleteElements({ nodes: [{ id: nodeItem?.id }], edges: connectedEdges });
    } else {
      deleteElements({ nodes: [{ id: nodeItem?.id }] });
    }
    setNodeItem(null);
  }, [
    connectedEdges,
    deleteElements,
    nodeItem?.id,
    setNodeItem,
    variant,
    variantConfig.supportsNotes
  ]);

  const handleFacilityChange = useCallback(
    (value: string) => {
      if (isNewFacilitySelection(value)) {
        setShowFacilityRegister(true);
        return;
      }
      setValue("warehouseName", value);
    },
    [setValue]
  );

  const handleFacilityModalClose = useCallback(
    (open: boolean, facilityData?: { newFacilityId?: string }) => {
      setShowFacilityRegister(open);
      if (facilityData?.newFacilityId) {
        newlyCreatedFacilityRef.current = facilityData.newFacilityId;
        void dropdownFacilitiesQuery.refetch?.().then(() => {
          setValue("warehouseName", facilityData.newFacilityId || "");
        });
      }
    },
    [dropdownFacilitiesQuery, setValue]
  );

  const onSubmit = useCallback(
    (values: InternalStorageFormValues) => {
      const storageNameChanged = getValues("internalStorageName");

      if (
        variantConfig.validateNodeLabel &&
        existingStorageName !== storageNameChanged &&
        chartNodes &&
        storageNameChanged &&
        isNodeLabelExists(chartNodes as any, storageNameChanged)
      ) {
        clearErrors("internalStorageName");
        setFocus("internalStorageName");
        setError("internalStorageName", {
          type: "manual",
          message: NodeLabelExistError.INTERNAL_STORAGE
        });
        return;
      }

      if (!values.warehouseName) {
        errorView(
          variant === "supplier"
            ? "Please select warehouse name"
            : "Please select a warehouse before saving!"
        );
        return;
      }

      const payload = normalizeInternalStoragePayload(
        buildInternalStoragePayload({
          values,
          variant,
          gateId,
          internalStorageId,
          originalStorageName: originalData?.internalStorageName
        }),
        gateConfig.gateIdKey,
        gateId
      );

      const hasReference = Boolean(nodeItem?.data?.reference?.internalStorageId);

      if (hasReference) {
        setIsSubmitting(true);
        updateInternalStorage(payload, {
          onSuccess: () => {
            const storageId = String(nodeItem?.data?.reference?.internalStorageId || internalStorageId);
            setLoadedData(mapFormValuesToLoadedData(values, storageId, variant));
            loadedStorageIdRef.current = storageId;

            const updatedNode = {
              ...nodeItem,
              data: {
                ...nodeItem?.data,
                label: buildInternalStorageNodeLabel(values.internalStorageName)
              }
            };
            void updateDrawingNode(updatedNode, "Internal Storage updated successfully").finally(
              () => setIsSubmitting(false)
            );
          },
          onError: () => setIsSubmitting(false)
        });
        return;
      }

      setIsSubmitting(true);
      createInternalStorage(payload, {
        onSuccess: (responseData: any) => {
          const storageId = String(responseData?._id || responseData?.id || "");
          if (storageId) {
            loadedStorageIdRef.current = storageId;
            setSavedInternalStorageId(storageId);
            setLoadedData(mapFormValuesToLoadedData(values, storageId, variant));
          }
          const updatedNode = {
            ...nodeItem,
            data: {
              ...nodeItem?.data,
              label: buildInternalStorageNodeLabel(values.internalStorageName),
              reference: {
                internalStorageId: storageId
              }
            }
          };
          void updateDrawingNode(updatedNode, "Internal Storage created successfully").finally(
            () => setIsSubmitting(false)
          );
        },
        onError: () => setIsSubmitting(false)
      });
    },
    [
      chartNodes,
      clearErrors,
      createInternalStorage,
      existingStorageName,
      gateConfig.gateIdKey,
      gateId,
      getValues,
      internalStorageId,
      nodeItem,
      originalData?.internalStorageName,
      setError,
      setFocus,
      updateDrawingNode,
      updateInternalStorage,
      variant,
      variantConfig.validateNodeLabel
    ]
  );

  const handleDelete = useCallback(() => {
    const dataToDelete = {
      [gateConfig.gateIdKey]: gateId,
      internalStorageId: nodeItem?.data?.reference?.internalStorageId
    };

    if (nodeItem?.data?.reference?.internalStorageId) {
      deleteInternalStorage(dataToDelete, {
        onSuccess: () => {
          onNodesDelete();
          setVisibleDeleteModal(false);
          successMessage("Internal Storage deleted successfully");
        },
        onError: (error: any) => {
          errorView(error?.msg || "Failed to delete internal storage");
        }
      });
      return;
    }

    onNodesDelete();
    setVisibleDeleteModal(false);
    successMessage("Internal Storage deleted successfully");
  }, [
    deleteInternalStorage,
    gateConfig.gateIdKey,
    gateId,
    nodeItem,
    onNodesDelete
  ]);

  const isInitialLoading =
    isLoadingData ||
    dropdownFacilitiesQuery.isLoading ||
    (variantConfig.usesSearchFacility && searchFacilitiesQuery.isLoading);
  const isSaving = isSubmitting || createLoading || updateLoading || deleteLoading;

  return {
    variant,
    variantConfig,
    gateConfig,
    isInitialLoading,
    isSaving,
    isFormDisabled,
    saveDisabled,
    detailsExpanded,
    setDetailsExpanded,
    metricsExpanded,
    setMetricsExpanded,
    notesExpanded,
    setNotesExpanded,
    form,
    register,
    control,
    errors,
    handleSubmit,
    onSubmit,
    notesRef,
    internalStorageId,
    notesParentId: internalStorageId,
    notesDisabled: variantConfig.supportsNotes && !existingStorageName,
    facilities,
    searchFacilities,
    facilityLoading: dropdownFacilitiesQuery.isLoading || searchFacilitiesQuery.isLoading,
    handleFacilityChange,
    handleFacilityModalClose,
    showFacilityRegister,
    visibleFacilityModal,
    setVisibleFacilityModal,
    watchedWarehouseName,
    isDefaultDataType,
    originalData,
    visibleDeleteModal,
    setVisibleDeleteModal,
    handleDelete,
    lockStatus,
    deleteLockStatus,
    refetchFacilityList: dropdownFacilitiesQuery.refetch,
    saveTitle: nodeItem?.data?.reference?.internalStorageId ? "Update" : "Save",
    nameDisabled:
      variantConfig.lockNameWhenSaved && Boolean(nodeItem?.data?.reference?.internalStorageId),
    showDeleteButton: variant === "simulate" ? Boolean(nodeItem?.data?.reference) : true
  };
};
